import { faEllipsis, faPen, faXmark, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

import { PrintersModel } from '../../models/Printers';
import { useTogglePrinter } from '../../hooks/useTogglePrinter';
import { usePermissions } from '@shared/hooks/usePermissions';

interface DropdownActionsProps {
  printers: PrintersModel;
  onEdit?: (printers: PrintersModel) => void;
  onRefresh?: () => void;
}

export const DropdownActions = ({ printers, onEdit, onRefresh }: DropdownActionsProps) => {
  const { t } = useTranslation('printersModule');
  const { togglePrinter } = useTogglePrinter();
  const { canAccess } = usePermissions();
  const isActive = printers.active;

  // 🎯 Verificar permisos granulares
  const canEdit = canAccess(['tenant', 'tenant_3pl'], ['printers.edit']);
  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['printers.create']);
  const canDelete = canAccess(['tenant', 'tenant_3pl'], ['printers.delete']);

  // Si no tiene ningún permiso de acción, no mostrar el dropdown
  if (!canEdit && !canCreate && !canDelete) return null;

  const handleToggle = () => {
    // Si se va a activar (isActive es false), requiere printers.create
    // Si se va a desactivar (isActive es true), requiere printers.delete
    if (!isActive && !canCreate) return;
    if (isActive && !canDelete) return;

    togglePrinter(printers, onRefresh);
  };

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="hover:bg-muted rounded-md" data-testid={`dropdown-trigger-${printers.id}`}>
            <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-44 border-card-border"
          data-testid={`dropdown-content-${printers.id}`}
        >
          {isActive && onEdit && canEdit && (
            <DropdownMenuItem data-testid={`dropdown-edit-${printers.id}`} onClick={() => onEdit(printers)}>
              <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
              {t('table.edit')}
            </DropdownMenuItem>
          )}

          {((!isActive && canCreate) || (isActive && canDelete)) && (
            <DropdownMenuItem data-testid={`dropdown-toggle-${printers.id}`} onClick={handleToggle}>
              <FontAwesomeIcon icon={isActive ? faXmark : faCheck} className="mr-2 w-3.5" />
              {isActive ? t('table.disable') : t('table.enable')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
